import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import api from '../services/api';

export interface AutoApplication {
  id: number;
  apply_method: string;
  apply_status: string;
  can_retry: boolean;
  apply_details: Record<string, unknown>;
  schedule_type: 'scheduled' | 'unscheduled' | 'unknown';
  cover_letter_used: string | null;
  title: string;
  company: string;
  location: string;
  source: string;
  source_url: string;
  rate: string | null;
  created_at: string;
}

export interface BookedSlot {
  id: number;
  worker_id: number;
  scraped_job_id: number;
  auto_application_id: number | null;
  start_time: string;
  end_time: string;
  status: string;
  title: string;
  company: string;
  location: string;
  source: string;
  source_url: string;
  rate: string | null;
}

export interface AutoApplicationStats {
  total_applications: number;
  pending_applications: number;
  submitted_applications: number;
  failed_applications: number;
  unknown_applications?: number;
  partial_applications?: number;
  skipped_applications?: number;
  total_activity?: number;
  applications_this_week: number;
  activity_this_week?: number;
}

export interface AutoApplicationsState {
  applications: AutoApplication[];
  selectedApplication: AutoApplication | null;
  stats: AutoApplicationStats | null;
  bookedSlots: BookedSlot[];
  loading: boolean;
  error: string | null;
  retryError: string | null;
}

const initialState: AutoApplicationsState = {
  applications: [],
  selectedApplication: null,
  stats: null,
  bookedSlots: [],
  loading: false,
  error: null,
  retryError: null,
};

export const fetchApplications = createAsyncThunk(
  'autoApplications/fetchApplications',
  async (
    filters: {
      apply_status?: string;
      apply_method?: string;
      limit?: number;
      offset?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) params.append(key, String(value));
      });

      const response = await api.get(`/auto-applications?${params.toString()}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch applications');
      }
      return rejectWithValue('Failed to fetch applications');
    }
  }
);

export const fetchApplicationStats = createAsyncThunk(
  'autoApplications/fetchApplicationStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/auto-applications/stats');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch application stats');
      }
      return rejectWithValue('Failed to fetch application stats');
    }
  }
);

export const fetchBookedSlots = createAsyncThunk(
  'autoApplications/fetchBookedSlots',
  async (limit: number = 10, { rejectWithValue }) => {
    try {
      const response = await api.get(`/worker-profile/schedule/upcoming?limit=${limit}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch booked slots');
      }
      return rejectWithValue('Failed to fetch booked slots');
    }
  }
);

export const retryApplication = createAsyncThunk(
  'autoApplications/retryApplication',
  async (applicationId: number, { rejectWithValue }) => {
    try {
      const response = await api.post(`/auto-applications/${applicationId}/retry`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to retry application');
      }
      return rejectWithValue('Failed to retry application');
    }
  }
);

export const fetchApplicationById = createAsyncThunk(
  'autoApplications/fetchApplicationById',
  async (applicationId: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/auto-applications/${applicationId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Application not found');
      }
      return rejectWithValue('Application not found');
    }
  }
);

const autoApplicationsSlice = createSlice({
  name: 'autoApplications',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchApplications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.loading = false;
        state.applications = action.payload;
      })
      .addCase(fetchApplications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchApplicationStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApplicationStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchApplicationStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchBookedSlots.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBookedSlots.fulfilled, (state, action) => {
        state.loading = false;
        state.bookedSlots = action.payload;
      })
      .addCase(fetchBookedSlots.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(retryApplication.pending, (state) => {
        state.loading = true;
        state.retryError = null;
      })
      .addCase(retryApplication.fulfilled, (state) => {
        state.loading = false;
        state.retryError = null;
      })
      .addCase(retryApplication.rejected, (state, action) => {
        state.loading = false;
        // Retrying is an action on an application that has already loaded.
        // Keep its failure separate from fetch/not-found state so a safe 4xx
        // response cannot replace valid application details with a 404 view.
        state.retryError = action.payload as string;
      })
      .addCase(fetchApplicationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApplicationById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedApplication = action.payload;
      })
      .addCase(fetchApplicationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default autoApplicationsSlice.reducer;
